import {FlowBlock, FlowBlockId, FlowPort, FlowWire} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';

export enum VisualDataDirection
{
    Northbound = 'Northbound',
    Southbound = 'Southbound'
}

export type FrameComponentId = FlowBlockId | BoxId;

export interface IVisualStreamId
{
    isStreamSet:boolean;
    uniqueKey:string;
}

export type VisualStreamKey = string;

export class VisualStreamId implements IVisualStreamId
{
    private constructor(public readonly frameComponentId:FrameComponentId,
                        public readonly direction:VisualDataDirection)
    {
    }

    public get isStreamSet():boolean
    {
        return false;
    }

    public get uniqueKey():VisualStreamKey
    {
        return VisualStreamId.generateUniqueKey(this.frameComponentId, this.direction);
    }

    public static forBlockSouthbound(blockId:FlowBlockId)
    {
        return new VisualStreamId(blockId, VisualDataDirection.Southbound);
    }

    public static forBoxNorthbound(boxId:BoxId)
    {
        return new VisualStreamId(boxId, VisualDataDirection.Northbound);
    }

    public static fromFrame(frame:IFrame, direction:VisualDataDirection):IVisualStreamId
    {
        const componentId = VisualStreamId.extractComponentId(frame);

        return new VisualStreamId(componentId, direction);
    }

    public static extractComponentId(frame:IFrame):FrameComponentId
    {
        const meta = frame.getMeta();
        const componentId:FrameComponentId = meta.getValue('component_id');

        return componentId;
    }

    public static generateUniqueKey(frameComponentId:FrameComponentId, direction:VisualDataDirection)
    {
        return `${frameComponentId}-${direction}`;
    }

    public toString():string
    {
        return `frameComponentId: ${this.frameComponentId} dir: ${this.direction}`;
    }
}

export class VisualStreamIdSet implements IVisualStreamId
{
    private _streamIds:VisualStreamId[] = [];

    constructor()
    {
    }

    public getStreamIds():VisualStreamId[]
    {
        return this._streamIds;
    }

    public addStreamId(id:VisualStreamId)
    {
        this._streamIds.push(id);
    }

    public get isStreamSet():boolean
    {
        return true;
    }

    public get uniqueKey():VisualStreamKey
    {
        let key = '';

        for( let streamId of this._streamIds )
            key += streamId.uniqueKey + '-';

        return key;
    }

    public toString():string
    {
        let sb = '';

        for( let streamId of this._streamIds )
            sb += streamId.toString() + ', ';

        return sb;
    }
}

export class BlockVisualStreams
{
    public constructor(public readonly input:IVisualStreamId | null,
                       public readonly output:IVisualStreamId | null)
    {
    }
}

export class VisualStreamIds
{
    public static forWire(wire:FlowWire):VisualStreamId
    {
        if( !wire.target || !wire.target.block )
            return null;

        const targetBlock = wire.target.block;

        if( targetBlock.isVisualLink )
            return VisualStreamId.forBlockSouthbound(targetBlock.id);
        else
        {
            let sourceAnchor = wire.source;
            let sourceBlock = sourceAnchor.block;
            let sourcePort = sourceAnchor.port;

            if( !sourceBlock || !sourcePort )
                return null;

            const foundDebugWire = sourceBlock.outWires.find((w:FlowWire) =>
            {
                return w.target.block && w.target.block.isDebugBlock &&
                    w.source.port && w.source.port.isEqualTo(sourcePort);
            });

            if( !foundDebugWire )
                return null;

            return VisualStreamId.forBlockSouthbound(foundDebugWire.target.block.id);
        }
    }

    public static forBlock(block:FlowBlock):BlockVisualStreams
    {
        let inputStreamSet:VisualStreamIdSet = new VisualStreamIdSet();
        let outputStreamSet:VisualStreamIdSet = new VisualStreamIdSet();

        this._addInputStreamIds(block, inputStreamSet);
        this._addOutputStreamIds(block, outputStreamSet);

        return new BlockVisualStreams(inputStreamSet, outputStreamSet);
    }

    protected static _addOutputStreamIds(block:FlowBlock, outputStreamSet:VisualStreamIdSet)
    {
        if( block.isVisualLink )
        {
            const streamId = VisualStreamIds._forBoxIdNorthbound(block.visualLink.boxId);
            outputStreamSet.addStreamId(streamId);
        }
        else
        {
            block.outputPorts.map((outPort:FlowPort) =>
            {
                const streamId = VisualStreamIds._findStreamIdForDebugBlock(block, outPort);
                if( streamId )
                    outputStreamSet.addStreamId(streamId);
            });
        }
    }

    protected static _addInputStreamIds(block:FlowBlock, inputStreamSet:VisualStreamIdSet)
    {
        if( block.inWires )
        {
            block.inWires.map((inWire:FlowWire) =>
            {
                const sourceBlock = inWire.source.block;

                if( sourceBlock.isDebugBlock )
                    return;

                if( sourceBlock )
                {
                    if( sourceBlock.isVisualLink )
                    {
                        const boxId = sourceBlock.visualLink.boxId;
                        const streamId = VisualStreamIds._forBoxIdNorthbound(boxId);

                        inputStreamSet.addStreamId(streamId);
                    }
                    else
                    {
                        const streamId = this._findStreamIdForDebugBlock(sourceBlock, inWire.source.port);
                        if( streamId )
                            inputStreamSet.addStreamId(streamId);
                    }
                }
            });
        }
    }

    protected static _findStreamIdForDebugBlock(cloudBlock:FlowBlock, cloudOutputPort:FlowPort):VisualStreamId
    {
        let retStreamId:VisualStreamId = null;

        cloudBlock.outWires.map((outWire:FlowWire) =>
        {
            const sourceBlockOutPort = outWire.source.port;

            if( sourceBlockOutPort &&
                sourceBlockOutPort.isEqualTo(cloudOutputPort) )
            {
                const targetBlock = outWire.target.block;

                if( targetBlock && targetBlock.isDebugBlock )
                    retStreamId = VisualStreamIds._forBlockSouthbound(targetBlock);
            }
        });

        return retStreamId;
    }

    private static _forBlockSouthbound(block:FlowBlock):VisualStreamId
    {
        return VisualStreamId.forBlockSouthbound(block.id);
    }

    private static _forBoxIdNorthbound(boxId:BoxId):VisualStreamId
    {
        return VisualStreamId.forBoxNorthbound(boxId);
    }
}

